import { supabase } from '@/lib/supabase';
import { InvestigadorRecord, Publicacion } from '../types';

// ─── Helpers de mapeo ───────────────────────────────────────────────────────

interface DbInvestigador {
  id: string;
  fecha_registro: string;
  nombre: string;
  apellido: string;
  email: string;
  telefono: string | null;
  orcid: string | null;
  unidad: string;
  postgrado: string | null;
  tipo_usuario: string;
  cargo: string;
  carrera_docente: string | null;
  materia_docente: string | null;
  cargo_admin_detalle: string | null;
  carrera_estudiante: string | null;
  nivel_estudiante: string | null;
  publicaciones?: DbPublicacion[];
}

interface DbPublicacion {
  id: string;
  investigador_id: string;
  titulo: string;
  link: string | null;
  base_datos: string | null;
  base_datos_otra: string | null;
  archivo_nombre: string | null;
  archivo_url: string | null;
  orden: number;
}

function dbToRecord(inv: DbInvestigador): InvestigadorRecord {
  const pubs: Publicacion[] = (inv.publicaciones ?? []).map((p) => ({
    id: p.id,
    titulo: p.titulo,
    link: p.link ?? '',
    baseDatos: p.base_datos ?? '',
    baseDatosOtra: p.base_datos_otra ?? '',
    archivoNombre: p.archivo_nombre ?? '',
    archivoBase64: '',
    archivoUrl: p.archivo_url ?? '',
  }));

  return {
    id: inv.id,
    fechaRegistro: new Date(inv.fecha_registro).toLocaleDateString('es-BO'),
    nombre: inv.nombre,
    apellido: inv.apellido,
    email: inv.email,
    telefono: inv.telefono ?? '',
    orcid: inv.orcid ?? '',
    unidad: inv.unidad,
    postgrado: inv.postgrado ?? '',
    tipoUsuario: inv.tipo_usuario,
    cargo: inv.cargo,
    carreraDocente: inv.carrera_docente ?? '',
    materiaDocente: inv.materia_docente ?? '',
    cargoAdminDetalle: inv.cargo_admin_detalle ?? '',
    carreraEstudiante: inv.carrera_estudiante ?? '',
    nivelEstudiante: inv.nivel_estudiante ?? '',
    publicaciones: pubs,
  };
}

// ─── Upload de archivo al bucket CENSO ──────────────────────────────────────

const BUCKET = 'CENSO';

interface UploadResult {
  url: string | null;
  error: string | null;
}

/**
 * Determina el Content-Type correcto a partir del nombre de archivo.
 * SIEMPRE prioriza la extensión del archivo sobre el MIME reportado por el navegador,
 * ya que en Windows los archivos .doc/.docx suelen reportarse como 'application/octet-stream'.
 */
function resolveContentType(file: File): string {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const mimeMap: Record<string, string> = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    odt: 'application/vnd.oasis.opendocument.text',
    txt: 'text/plain',
    zip: 'application/zip',
    rar: 'application/x-rar-compressed',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
  };
  // Prioridad: extensión del archivo (más confiable que el MIME del navegador)
  if (mimeMap[ext]) return mimeMap[ext];
  // Fallback: MIME del navegador si no es genérico
  if (file.type && file.type !== 'application/octet-stream') return file.type;
  return 'application/octet-stream';
}

/** Tiempo máximo de espera para el upload (ms). Evita que el formulario se quede colgado. */
const UPLOAD_TIMEOUT_MS = 90_000; // 90 segundos — soporta archivos Word pesados en conexiones lentas

/**
 * Envuelve una promesa con un timeout.
 * Si la promesa no resuelve en `ms` milisegundos, rechaza con un error de timeout.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`Timeout: "${label}" tardó más de ${ms / 1000}s. Verifique la conexión o el tamaño del archivo.`));
    }, ms);

    promise
      .then((val) => { clearTimeout(timer); resolve(val); })
      .catch((err) => { clearTimeout(timer); reject(err); });
  });
}

/**
 * Sube un File nativo al bucket CENSO de Supabase Storage.
 * Incluye timeout de 45 segundos para evitar que el formulario quede colgado.
 * Retorna { url, error } con detalle del problema si falla.
 */
async function uploadArchivoToCENSO(
  archivoFile: File,
  emailInvestigador: string
): Promise<UploadResult> {
  try {
    const contentType = resolveContentType(archivoFile);
    const timestamp = Date.now();
    const safeEmail = emailInvestigador.replace(/[^a-zA-Z0-9]/g, '_');
    const safeName = archivoFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${safeEmail}/${timestamp}_${safeName}`;

    const ext = archivoFile.name.split('.').pop()?.toLowerCase() ?? '';
    console.log(
      `[CENSO] Iniciando upload → ${path}`,
      `(ext: .${ext}, MIME forzado: ${contentType}, tamaño: ${(archivoFile.size / 1024).toFixed(1)} KB)`
    );

    // Upload con timeout de 45s — evita que se quede procesando indefinidamente
    const uploadPromise = supabase.storage
      .from(BUCKET)
      .upload(path, archivoFile, {
        contentType,
        upsert: false,
      });

    const { data: uploadData, error: uploadError } = await withTimeout(
      uploadPromise,
      UPLOAD_TIMEOUT_MS,
      `upload de "${archivoFile.name}"`
    );

    if (uploadError) {
      const msg = uploadError.message ?? JSON.stringify(uploadError);
      console.error(`[CENSO] Upload fallido: ${msg}`);

      if (msg.includes('Bucket not found') || msg.includes('bucket') || msg.includes('404')) {
        return {
          url: null,
          error: `El bucket "${BUCKET}" no existe en Supabase Storage. El encargado debe crearlo como público en el panel de Supabase.`,
        };
      }
      if (msg.includes('not authorized') || msg.includes('403') || msg.includes('policy') || msg.includes('Unauthorized')) {
        return {
          url: null,
          error: `Sin permisos para subir archivos. El encargado debe revisar las políticas del bucket "${BUCKET}" en Supabase Storage.`,
        };
      }
      if (msg.includes('already exists') || msg.includes('duplicate')) {
        return { url: null, error: 'Ya existe un archivo con ese nombre. Intente nuevamente en unos segundos.' };
      }
      if (msg.includes('exceeded') || msg.includes('too large') || msg.includes('size')) {
        return {
          url: null,
          error: `El archivo supera el límite del plan de Supabase Storage. Contacte al administrador.`,
        };
      }
      return { url: null, error: `Error al subir archivo: ${msg}` };
    }

    console.log(`[CENSO] Upload exitoso: ${uploadData?.path ?? path}`);

    const { data: urlData } = supabase.storage
      .from(BUCKET)
      .getPublicUrl(uploadData?.path ?? path);

    const publicUrl = urlData?.publicUrl ?? null;
    console.log(`[CENSO] URL pública: ${publicUrl}`);

    if (!publicUrl) {
      return {
        url: null,
        error: 'El archivo se subió pero no se pudo obtener la URL pública. Verifique que el bucket sea público.',
      };
    }

    return { url: publicUrl, error: null };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error(`[CENSO] Excepción inesperada: ${msg}`);

    // Error de timeout — mensaje claro al usuario
    if (msg.includes('Timeout') || msg.includes('tardó')) {
      return {
        url: null,
        error: `El archivo tardó demasiado en subirse. Esto puede deberse a conexión lenta o a que Supabase Storage no está configurado aún. El registro se guardará sin el archivo adjunto.`,
      };
    }

    return { url: null, error: `Error inesperado al subir archivo: ${msg}` };
  }
}

// ─── API pública ────────────────────────────────────────────────────────────

export interface SaveResult {
  ok: boolean;
  /** Error general del formulario */
  error?: string;
  /** Errores por publicación (índice → mensaje de error del archivo) */
  archivoErrors?: Record<number, string>;
}

/**
 * Guarda un investigador junto con sus publicaciones en Supabase.
 * Sube los archivos adjuntos al bucket CENSO.
 * Retorna { ok, error, archivoErrors } con detalle completo.
 */
export const saveInvestigador = async (
  record: InvestigadorRecord
): Promise<SaveResult> => {
  // 1. Insertar investigador
  const { data: invData, error: invError } = await supabase
    .from('investigadores')
    .insert({
      nombre: record.nombre,
      apellido: record.apellido,
      email: record.email,
      telefono: record.telefono || null,
      orcid: record.orcid || null,
      unidad: record.unidad,
      postgrado: record.postgrado || null,
      tipo_usuario: record.tipoUsuario,
      cargo: record.cargo,
      carrera_docente: record.carreraDocente || null,
      materia_docente: record.materiaDocente || null,
      cargo_admin_detalle: record.cargoAdminDetalle || null,
      carrera_estudiante: record.carreraEstudiante || null,
      nivel_estudiante: record.nivelEstudiante || null,
    })
    .select('id')
    .maybeSingle();

  if (invError || !invData) {
    const msg = invError?.message ?? 'Error desconocido al guardar el investigador';
    console.error('[saveInvestigador] Error al insertar investigador:', invError);
    return { ok: false, error: msg };
  }

  // 2. Procesar publicaciones — subir archivos al bucket CENSO si corresponde
  const archivoErrors: Record<number, string> = {};

  // Filtrar publicaciones vacías (título, link, base de datos y archivo todos en blanco)
  const publicacionesValidas = record.publicaciones.filter(
    (p) => p.titulo.trim() || p.link.trim() || p.baseDatos.trim() || p.archivoNombre.trim()
  );

  if (publicacionesValidas.length > 0) {
    const pubRowsPromises = publicacionesValidas.map(async (p, idx) => {
      let archivoUrl: string | null = null;

      // Prioridad 1: File nativo (nuevo flujo — soporta archivos grandes)
      // Prioridad 2: base64 legacy (por compatibilidad, no debería usarse ya)
      const hasFile = p.archivoFile instanceof File;
      const hasBase64 = !hasFile && p.archivoNombre && p.archivoBase64;

      if (hasFile && p.archivoFile) {
        const result = await uploadArchivoToCENSO(p.archivoFile, record.email);
        if (result.error) {
          archivoErrors[idx] = result.error;
          console.warn(`[saveInvestigador] Pub #${idx + 1} — archivo no subido: ${result.error}`);
        }
        archivoUrl = result.url;
      } else if (hasBase64) {
        // Fallback legacy: convertir base64 a File y subir igual
        try {
          const ext = p.archivoNombre.split('.').pop()?.toLowerCase() ?? 'bin';
          const mimeMap: Record<string, string> = {
            pdf: 'application/pdf',
            doc: 'application/msword',
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          };
          const mime = mimeMap[ext] ?? 'application/octet-stream';
          const byteChars = atob(p.archivoBase64);
          const byteNums = new Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
          const blob = new Blob([new Uint8Array(byteNums)], { type: mime });
          const legacyFile = new File([blob], p.archivoNombre, { type: mime });
          const result = await uploadArchivoToCENSO(legacyFile, record.email);
          if (result.error) {
            archivoErrors[idx] = result.error;
          }
          archivoUrl = result.url;
        } catch (legacyErr) {
          archivoErrors[idx] = `Error en conversión legacy: ${legacyErr instanceof Error ? legacyErr.message : String(legacyErr)}`;
        }
      }

      return {
        investigador_id: invData.id,
        titulo: p.titulo,
        link: p.link || null,
        base_datos: p.baseDatos || null,
        base_datos_otra: p.baseDatosOtra || null,
        archivo_nombre: p.archivoNombre || null,
        archivo_url: archivoUrl,
        orden: idx,
      };
    });

    const pubRows = await Promise.all(pubRowsPromises);

    const { error: pubError } = await supabase
      .from('publicaciones')
      .insert(pubRows);

    if (pubError) {
      console.error('[saveInvestigador] Error al insertar publicaciones:', pubError);
      // El investigador ya se guardó — devolvemos ok:true pero con advertencia
      return {
        ok: true,
        error: `El registro se guardó pero hubo un problema al guardar las publicaciones: ${pubError.message}`,
        archivoErrors: Object.keys(archivoErrors).length > 0 ? archivoErrors : undefined,
      };
    }
  }

  return {
    ok: true,
    archivoErrors: Object.keys(archivoErrors).length > 0 ? archivoErrors : undefined,
  };
};

/**
 * Obtiene todos los investigadores con sus publicaciones.
 * Solo funciona para usuarios autenticados (admin).
 */
export const getInvestigadores = async (): Promise<InvestigadorRecord[]> => {
  const { data, error } = await supabase
    .from('investigadores')
    .select(`
      *,
      publicaciones (*)
    `)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[getInvestigadores] Error:', error);
    return [];
  }

  return (data as DbInvestigador[]).map(dbToRecord);
};

/**
 * Obtiene todos los registros del investigador según su email.
 */
export const getRegistrosByEmail = async (
  email: string
): Promise<InvestigadorRecord[]> => {
  const { data, error } = await supabase
    .from('investigadores')
    .select(`
      *,
      publicaciones (*)
    `)
    .eq('email', email)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[getRegistrosByEmail] Error:', error);
    return [];
  }

  return (data as DbInvestigador[]).map(dbToRecord);
};

/**
 * Elimina todos los investigadores (y sus publicaciones por CASCADE).
 */
export const clearInvestigadores = async (): Promise<boolean> => {
  const { error } = await supabase
    .from('investigadores')
    .delete()
    .neq('id', '00000000-0000-0000-0000-000000000000');

  if (error) {
    console.error('[clearInvestigadores] Error:', error);
    return false;
  }

  return true;
};
